import { defineComponent, toRaw, toRef, ref, watch, WatchStopHandle } from 'vue';
import TinyMceEditor from '@tinymce/tinymce-vue';
import { Editor } from 'tinymce';

import { HtmlFormField } from './html-form-field';
import { HtmlSanitizer } from './html-sanitizer';
import { WordCountPlugin } from '@/platform/controls/tinymce/word-count-plugin';
import { HtmlFormattingOptions } from './html-formatting-options';

export default defineComponent({
	components: {
		TinyMceEditor,
	},
	props: {
		modelValue: HtmlFormField,
		formattingOptions: String,
	},
	emits: ['editorBlurred'],
	setup: (props, { emit }) => {
		const editorContent = ref<string>('');
		const modelValue = toRef(props, 'modelValue');

		const getField = (): HtmlFormField | undefined => {
			return toRaw(modelValue.value);
		};

		const formattingOptions = props.formattingOptions || '';
		const htmlFormattingOptions = new HtmlFormattingOptions(formattingOptions);

		const htmlSanitizer = new HtmlSanitizer(htmlFormattingOptions);

		let watchStopHandleModelValueContent: WatchStopHandle | null = null;
		const maintainModelValueContentWatch = (): void => {
			if (watchStopHandleModelValueContent) {
				watchStopHandleModelValueContent();
			}

			const field = getField();
			if (field === undefined) {
				return;
			}

			const maintainEditorContentValue = (): void => {
				field.content.value = htmlSanitizer.sanitize(field.content.value ?? '');

				if (editorContent.value !== field.content.value) {
					editorContent.value = field.content.value;
				}
			};

			maintainEditorContentValue();
			watchStopHandleModelValueContent = watch(field.content, () => maintainEditorContentValue());
		};

		maintainModelValueContentWatch();
		watch(modelValue, () => maintainModelValueContentWatch());

		const tinyMceFormattingOptions = htmlFormattingOptions.convertToTinyMceToolbar();

		const tinyMceOptions = {
			skin: false,
			content_css: false,
			selector: 'textarea',
			plugins: 'lists wordcount',
			toolbar: `undo | ${tinyMceFormattingOptions} | removeformat | wordcount`,
			menubar: 'false',
			setup: (editor: Editor): void => {
				editor.on('blur', () => {
					const field = getField();
					if (field === undefined) {
						return;
					}

					const pluginWordCount = editor.plugins.wordcount as WordCountPlugin;

					field.replaceContent(htmlSanitizer.sanitize(editorContent.value));
					field.numberOfCharacters = pluginWordCount.body.getCharacterCount();

					editorContent.value = field.content.value;
					emit('editorBlurred');
				});
			},
		};

		return {
			editorContent,
			tinyMceOptions,
		};
	},
});
